import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Check, X, ExternalLink, Loader2, CheckCircle, AlertCircle, Clock, Package, Flame, Calendar, User, Truck, FileCheck, Zap } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Logo } from '../components/Logo';

const ScrollReveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.classList.add('revealed'), delay);
          obs.unobserve(el);
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return (
    <div ref={ref} className={`scroll-reveal ${className}`}>
      {children}
    </div>
  );
};

// Animated Counter Component
const CountUp = ({ end, suffix = '', color = '#F5820D', delay = 0 }) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setTimeout(() => setHasStarted(true), delay);
          obs.unobserve(el);
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay, hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;
    const duration = 2000;
    const steps = 60;
    const increment = end / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [hasStarted, end]);

  const formatNumber = (num) => {
    if (num >= 10000) {
      return num.toLocaleString('fr-FR');
    }
    return num;
  };

  return (
    <div ref={ref}>
      <span 
        className="font-oswald text-4xl md:text-5xl font-bold"
        style={{ color }}
      >
        {formatNumber(count)}{suffix}
      </span>
    </div>
  );
};

const PACK_META = [
  {
    key: 'essential',
    number: '01',
    accent: '#CD853F',
    bg: '#161616',
    banner: null,
  },
  {
    key: 'performance',
    number: '02',
    accent: '#F5820D',
    bg: '#0C1E2D',
    banner: 'RECOMMANDÉ',
  },
  {
    key: 'premium',
    number: '03',
    accent: '#F5C842',
    bg: '#111111',
    banner: null,
  },
];

// Dağ silüeti noktaları (SVG viewBox: 0 0 1440 112)
const MOUNTAIN_POINTS = [
  [0, 82], [100, 12], [240, 74], [420, 4], [590, 68],
  [720, 26], [870, 72], [980, 8], [1110, 58], [1260, 18],
  [1390, 66], [1440, 44],
];

const HeroAnimation = () => {
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => { setScrollY(window.scrollY); rafId = null; });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (rafId) cancelAnimationFrame(rafId); };
  }, []);

  const wp = Math.max(0, Math.min(1, (scrollY - 80) / 400));
  const pinX = 5 + 87.5 * wp;
  const pinBottom = getMountainBottom(pinX);
  const extraPins = [
    { startX: 2,  speed: 0.72, scale: 0.8,  opacity: 0.8,  color: '#F5820D' },
    { startX: 12, speed: 0.52, scale: 0.7,  opacity: 0.7,  color: '#F5820D' },
    { startX: 6,  speed: 0.88, scale: 0.75, opacity: 0.75, color: '#F5820D' },
    { startX: 20, speed: 0.62, scale: 0.65, opacity: 0.6,  color: '#F5820D' },
  ];

  return (
    <>
      <div className="absolute bg-cover bg-center" style={{ backgroundImage: 'url(https://images.pexels.com/photos/2402777/pexels-photo-2402777.jpeg?auto=compress&cs=tinysrgb&w=1600)', top: '-15%', left: 0, right: 0, bottom: '-15%', transform: `translateY(${scrollY * 0.3}px)`, clipPath: `inset(0 0 0 ${pinX}%)`, willChange: 'transform' }} />
      <div className="absolute bg-cover bg-center" style={{ backgroundImage: 'url(https://images.pexels.com/photos/1571939/pexels-photo-1571939.jpeg?auto=compress&cs=tinysrgb&w=1600)', top: '-15%', left: 0, right: 0, bottom: '-15%', transform: `translateY(${scrollY * 0.3}px)`, clipPath: `inset(0 ${100 - pinX}% 0 0)`, willChange: 'transform' }} />
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/55 to-black/25" />
      {extraPins.map((ep, i) => {
        const extraWp = wp;
        const extraX = ep.startX + 87.5 * extraWp * ep.speed;
        const extraBottom = getMountainBottom(extraX);
        return (
          <div key={i} style={{ position: 'absolute', bottom: `${extraBottom}px`, left: `${extraX}%`, transform: `translateX(-50%) scale(${ep.scale})`, zIndex: 15 + i, pointerEvents: 'none', opacity: ep.opacity, filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.25))' }}>
            <svg width="22" height="30" viewBox="0 0 16 22" fill="none">
              <path d="M8 0C3.58 0 0 3.58 0 8C0 14 8 22 8 22C8 22 16 14 16 8C16 3.58 12.42 0 8 0Z" fill={ep.color}/>
              <circle cx="8" cy="8" r="3" fill="white"/>
            </svg>
          </div>
        );
      })}
      <div style={{ position: 'absolute', bottom: `${pinBottom}px`, left: `${pinX}%`, transform: 'translateX(-50%)', zIndex: 21, pointerEvents: 'none', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>
        <svg width="22" height="30" viewBox="0 0 16 22" fill="none">
          <path d="M8 0C3.58 0 0 3.58 0 8C0 14 8 22 8 22C8 22 16 14 16 8C16 3.58 12.42 0 8 0Z" fill="#F5820D"/>
          <circle cx="8" cy="8" r="3" fill="white"/>
        </svg>
      </div>
    </>
  );
};

// Verilen x yüzdesine karşılık dağ tepesinin hero altından yüksekliğini döner (px)
const getMountainBottom = (xPercent) => {
  const xSvg = (xPercent / 100) * 1440;
  for (let i = 0; i < MOUNTAIN_POINTS.length - 1; i++) {
    const [x1, y1] = MOUNTAIN_POINTS[i];
    const [x2, y2] = MOUNTAIN_POINTS[i + 1];
    if (xSvg >= x1 && xSvg <= x2) {
      const t = (xSvg - x1) / (x2 - x1);
      const y = y1 + t * (y2 - y1);
      return 112 - y; // hero altından yükseklik (px)
    }
  }
  return 46;
};

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ nom: '', prenom: '', email: '', event_name: '', participants: '', pack_choice: '', event_date: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    try {
      // TODO: Replace with real EmailJS credentials
      // await emailjs.send('YOUR_SERVICE_ID', 'YOUR_TEMPLATE_ID', { ...form, to_email: 'info@ultrasport.live' }, 'YOUR_PUBLIC_KEY');
      await new Promise(r => setTimeout(r, 1200));
      setSubmitStatus('success');
      setForm({ nom: '', prenom: '', email: '', event_name: '', participants: '', pack_choice: '', event_date: '', message: '' });
    } catch {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const packOptions = t('home.contact.pack_options', { returnObjects: true });
  const pains = t('home.problems.pains', { returnObjects: true });
  const solutions = t('home.problems.solutions', { returnObjects: true });

  const inputClass = 'w-full bg-white/5 border border-white/20 text-white placeholder-white/30 rounded-sm px-4 py-3 text-sm font-manrope focus:outline-none focus:border-[#F5820D] transition-colors';
  const labelClass = 'block text-white/90 text-xs font-oswald uppercase tracking-wider mb-1.5';

  return (
    <div className="min-h-screen bg-white">

      {/* HERO */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        <HeroAnimation />

        {/* Hero içerik */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto flex flex-col items-center">
          <div className="mb-4 animate-fade-in mt-20 md:mt-32" style={{ animationDelay: '0.1s' }}>
            <Logo size="lg" variant="white" />
          </div>
          <h1 className="font-oswald font-bold text-white leading-none mb-4 animate-fade-up" style={{ fontSize: 'clamp(2.5rem, 8vw, 5.5rem)', animationDelay: '0.35s' }}>
            {t('home.hero.title1')}<br />
            <span className="text-[#F5820D]">{t('home.hero.title2')}</span>
          </h1>
          <p className="font-manrope text-white/70 text-base md:text-lg max-w-xl mb-10 animate-fade-up" style={{ animationDelay: '0.55s' }}>
            {t('home.hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-up" style={{ animationDelay: '0.75s' }}>
            <Link
              to="/produits"
              className="bg-[#F5820D] hover:bg-[#D9730B] text-white font-oswald uppercase tracking-widest px-8 py-4 text-sm flex items-center gap-2 transition-all duration-300 hover:-translate-y-0.5"
              data-testid="hero-cta1"
            >
              {t('home.hero.cta1')} <ChevronRight size={16} />
            </Link>
            <Link
              to="/contact"
              className="border-2 border-[#F5820D] hover:bg-[#F5820D] hover:text-white text-white font-oswald uppercase tracking-widest px-8 py-4 text-sm transition-all duration-300 hover:-translate-y-0.5"
              data-testid="hero-cta2"
            >
              {t('home.hero.cta2')}
            </Link>
          </div>
        </div>

        {/* Mountain silhouette — ultra-realistic */}
        <div className="absolute bottom-0 left-0 right-0 h-28">
          <svg viewBox="0 0 1440 112" preserveAspectRatio="none" className="w-full h-full">
            <defs>
              {/* Uzak dağlar — atmosferik mavi sis */}
              <linearGradient id="mGradFar" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8AAFC4" stopOpacity="0.5"/>
                <stop offset="100%" stopColor="#B0CEDD" stopOpacity="0.12"/>
              </linearGradient>
              {/* Orta uzaklık dağlar */}
              <linearGradient id="mGradMid" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#5C7E94" stopOpacity="0.6"/>
                <stop offset="100%" stopColor="#7A9AAE" stopOpacity="0.25"/>
              </linearGradient>
              {/* Ana dağ — koyu kayalık tepeden beyaz tabana */}
              <linearGradient id="mGradMain" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1E2F3C"/>
                <stop offset="18%" stopColor="#2E4258"/>
                <stop offset="40%" stopColor="#4E6E82"/>
                <stop offset="62%" stopColor="#82A3B4"/>
                <stop offset="80%" stopColor="#BDD4DE"/>
                <stop offset="93%" stopColor="#E4F0F4"/>
                <stop offset="100%" stopColor="#FFFFFF"/>
              </linearGradient>
              {/* Kar gradyanı */}
              <linearGradient id="mGradSnow" x1="0.25" y1="0" x2="0.75" y2="1">
                <stop offset="0%" stopColor="#FFFFFF"/>
                <stop offset="60%" stopColor="#EAF3F8"/>
                <stop offset="100%" stopColor="#CCE0EC"/>
              </linearGradient>
            </defs>

            {/* Katman 1: Çok uzak, sisli dağlar — ana silüetin üstünde görünür */}
            <path d="M0 112 L0 55 L130 34 L280 58 L440 26 L610 50 L780 28 L950 52 L1110 30 L1290 50 L1440 40 L1440 112 Z"
                  fill="url(#mGradFar)"/>

            {/* Katman 2: Orta uzaklık dağlar */}
            <path d="M0 112 L0 64 L160 42 L330 66 L510 36 L690 58 L860 40 L1040 60 L1210 36 L1400 56 L1440 48 L1440 112 Z"
                  fill="url(#mGradMid)"/>

            {/* Ana ön dağ — gerçekçi gradyan dolgu */}
            <path d="M0 112 L0 82 L100 12 L240 74 L420 4 L590 68 L720 26 L870 72 L980 8 L1110 58 L1260 18 L1390 66 L1440 44 L1440 112 Z"
                  fill="url(#mGradMain)"/>

            {/* Gölge yüzler — her tepenin sağ/doğu yüzü (güneş soldan) */}
            <path d="M100 12 L220 74 L178 74 Z" fill="#111E28" opacity="0.38"/>
            <path d="M420 4 L565 68 L508 68 Z" fill="#111E28" opacity="0.42"/>
            <path d="M720 26 L848 72 L802 72 Z" fill="#111E28" opacity="0.36"/>
            <path d="M980 8 L1088 58 L1048 58 Z" fill="#111E28" opacity="0.40"/>
            <path d="M1260 18 L1378 66 L1334 66 Z" fill="#111E28" opacity="0.34"/>

            {/* Kar örtüsü — her büyük tepe için doğal, düzensiz şekil */}
            {/* Tepe 1: (100, 12) */}
            <path d="M100 12 L80 22 L87 29 L93 24 L100 31 L109 23 L116 31 L124 22 L119 15 L100 12 Z"
                  fill="url(#mGradSnow)"/>
            {/* Tepe 2: (420, 4) — en yüksek, en dramatik kar */}
            <path d="M420 4 L396 17 L403 24 L409 19 L417 27 L424 19 L432 27 L440 18 L447 25 L454 16 L447 8 L420 4 Z"
                  fill="url(#mGradSnow)"/>
            {/* Küçük karlı omuz — soldan gelen yamaçta */}
            <path d="M370 28 L362 34 L368 39 L376 33 L382 39 L388 32 L380 28 L370 28 Z"
                  fill="url(#mGradSnow)" opacity="0.75"/>
            {/* Tepe 3: (720, 26) */}
            <path d="M720 26 L702 37 L709 43 L716 37 L722 44 L730 36 L739 43 L746 34 L737 27 L720 26 Z"
                  fill="url(#mGradSnow)"/>
            {/* Tepe 4: (980, 8) */}
            <path d="M980 8 L959 20 L966 27 L973 21 L980 29 L988 21 L997 29 L1004 19 L997 11 L980 8 Z"
                  fill="url(#mGradSnow)"/>
            {/* Tepe 5: (1260, 18) */}
            <path d="M1260 18 L1242 29 L1249 35 L1256 29 L1263 36 L1271 28 L1279 36 L1286 26 L1277 19 L1260 18 Z"
                  fill="url(#mGradSnow)"/>
            {/* Küçük karlı çıkıntı — tepe 5 solunda */}
            <path d="M1218 36 L1210 43 L1216 47 L1222 42 L1228 47 L1232 41 L1224 36 L1218 36 Z"
                  fill="url(#mGradSnow)" opacity="0.7"/>

            {/* Kaya dokusu çizgileri — tepeler yakınında ince yarıklar */}
            <path d="M97 19 L93 44" stroke="#111E28" strokeWidth="0.7" opacity="0.13" strokeLinecap="round"/>
            <path d="M415 12 L411 46" stroke="#111E28" strokeWidth="0.7" opacity="0.13" strokeLinecap="round"/>
            <path d="M426 10 L431 44" stroke="#111E28" strokeWidth="0.6" opacity="0.10" strokeLinecap="round"/>
            <path d="M717 33 L713 58" stroke="#111E28" strokeWidth="0.7" opacity="0.13" strokeLinecap="round"/>
            <path d="M977 16 L973 44" stroke="#111E28" strokeWidth="0.7" opacity="0.13" strokeLinecap="round"/>
            <path d="M1257 26 L1253 52" stroke="#111E28" strokeWidth="0.6" opacity="0.11" strokeLinecap="round"/>
          </svg>
        </div>

        {/* FINISH sticker — sabit, bitiş noktasında */}
        <div style={{ position: 'absolute', bottom: `${getMountainBottom(88) + 6}px`, left: '88%', zIndex: 22, pointerEvents: 'none' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: '#F5C842', color: '#111111', fontSize: '9px', fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '3px 7px', borderRadius: '3px', boxShadow: '0 2px 6px rgba(0,0,0,0.5)', transform: 'rotate(5deg)', whiteSpace: 'nowrap' }}>
            ★ FINISH
          </span>
        </div>
      </section>

      {/* PROBLEM / SOLUTION */}
      {/* ========== STATS + 6 REASONS SECTION ========== */}
      <section className="bg-gradient-to-b from-white to-gray-100 py-10 md:py-20" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6">

          {/* Animated Stats */}
          <ScrollReveal>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-8 md:mb-16">
              {[
                { key: 'events', color: '#F5820D' },
                { key: 'delivery', color: '#1B4F6B' },
                { key: 'participants', color: '#C0392B' },
                { key: 'expertise', color: '#F5C842' },
              ].map((stat, i) => (
                <div key={i} className="text-center p-4 md:p-6 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <CountUp end={t(`home.stats.${stat.key}.value`)} suffix={t(`home.stats.${stat.key}.suffix`)} color={stat.color} delay={i * 150} />
                  <p className="font-manrope text-gray-600 text-sm mt-2">{t(`home.stats.${stat.key}.label`)}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>

          {/* 6 Reasons Title */}
          <ScrollReveal>
            <div className="text-center mb-6 md:mb-12">
              <div className="inline-flex items-center gap-3 mb-3">
                <div className="w-12 h-px bg-[#F5820D]" />
                <p className="font-oswald text-[#F5820D] text-xs uppercase tracking-widest">{t('home.why_choose.eyebrow')}</p>
                <div className="w-12 h-px bg-[#F5820D]" />
              </div>
              <h2 className="font-oswald text-2xl md:text-4xl font-bold uppercase text-[#111111]">
                {t('home.why_choose.title')}
              </h2>
            </div>
          </ScrollReveal>

          {/* 6 Reasons Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
            {[
              { icon: Calendar, key: 'planning', accent: '#F5820D' },
              { icon: CheckCircle, key: 'quality', accent: '#1B4F6B' },
              { icon: Zap, key: 'expert', accent: '#C0392B' },
              { icon: User, key: 'contact', accent: '#F5C842' },
              { icon: FileCheck, key: 'bat', accent: '#1B9BB0' },
              { icon: Truck, key: 'delivery', accent: '#F5820D' },
            ].map((reason, i) => (
              <ScrollReveal key={i} delay={i * 80}>
                <div
                  className="group relative p-3 md:p-6 rounded-lg border-2 border-gray-100 hover:border-transparent bg-white hover:shadow-xl transition-all duration-300 h-full"
                  style={{ '--accent': reason.accent }}
                >
                  <div
                    className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ background: `linear-gradient(135deg, ${reason.accent}08 0%, ${reason.accent}15 100%)` }}
                  />
                  <div className="relative z-10">
                    <reason.icon size={28} style={{ color: reason.accent }} className="mb-2 md:mb-3" />
                    <h3 className="font-oswald text-sm md:text-lg font-bold uppercase text-[#111111] mb-1 md:mb-2 group-hover:text-[#F5820D] transition-colors leading-tight">
                      {t(`home.reasons.${reason.key}.title`)}
                    </h3>
                    <p className="font-manrope text-gray-600 text-xs md:text-sm leading-relaxed hidden sm:block">
                      {t(`home.reasons.${reason.key}.desc`)}
                    </p>
                  </div>
                  <div
                    className="absolute bottom-0 left-0 right-0 h-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ backgroundColor: reason.accent }}
                  />
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* PACK PREVIEW */}
      <section
        className="relative overflow-hidden -mt-px"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/2402777/pexels-photo-2402777.jpeg?auto=compress&cs=tinysrgb&w=1600)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
          backgroundAttachment: 'fixed',
        }}
        data-testid="packs-section"
      >
        {/* Semi-transparent overlay — image shows through */}
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(5, 10, 15, 0.78)' }} />

        {/* PROMO BANNER - Inside packs section */}
        <div className="relative z-10 border-b border-[#F5820D]/30">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-5">
            <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 text-center">
              <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4">
                <span className="bg-[#F5820D] text-[#111] font-oswald text-xs md:text-sm font-bold uppercase px-3 py-1.5 rounded-sm tracking-wider">
                  {t('home.promo.badge')}
                </span>
                <span className="font-manrope text-white text-sm md:text-base">
                  {t('home.promo.text')}
                </span>
                <span className="font-oswald text-[#F5820D] text-xl md:text-2xl font-bold">
                  {t('home.promo.price')}
                </span>
              </div>
              <Link 
                to="/contact"
                className="bg-[#F5820D] hover:bg-[#D9730B] text-[#111] font-oswald text-xs md:text-sm font-bold uppercase px-5 md:px-6 py-2 rounded-sm transition-all duration-300 tracking-wider"
              >
                {t('home.promo.cta')} →
              </Link>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-12 md:py-24">
          <ScrollReveal>
            <div className="text-center mb-8 md:mb-16">
              <span className="inline-block font-oswald text-xs uppercase tracking-widest text-[#F5820D] border border-[#F5820D]/30 px-4 py-1.5 mb-5">
                NOTRE OFFRE
              </span>
              <h2 className="font-oswald font-bold uppercase text-white leading-none mb-4" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}>
                {t('home.packs.title')}
              </h2>
              <p className="font-manrope text-white/40 text-base max-w-xl mx-auto">
                {t('home.packs.subtitle')}
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-px" style={{ backgroundColor: '#222' }}>
            {PACK_META.map((meta, idx) => {
              const packData = t(`home.packs.${meta.key}`, { returnObjects: true });
              const items = t(`home.packs.${meta.key}.items`, { returnObjects: true });
              return (
                <ScrollReveal key={meta.key} delay={idx * 100}>
                  <div
                    className="relative overflow-hidden flex flex-col h-full group hover:-translate-y-1 transition-transform duration-300"
                    style={{ backgroundColor: meta.key === 'performance' ? 'rgba(12,30,45,0.88)' : 'rgba(16,16,16,0.88)', backdropFilter: 'blur(4px)' }}
                    data-testid={`pack-card-${meta.key}`}
                  >
                    {/* Huge watermark number */}
                    <div
                      className="absolute -bottom-6 -right-2 font-oswald font-bold leading-none select-none pointer-events-none"
                      style={{ fontSize: '220px', color: meta.accent, opacity: 0.04 }}
                    >
                      {meta.number}
                    </div>

                    {/* Top accent bar */}
                    <div style={{ height: '3px', backgroundColor: meta.accent }} />

                    {/* Banner */}
                    {meta.banner && (
                      <div
                        className="text-[#111] text-base md:text-lg font-oswald font-bold uppercase tracking-widest text-center py-3 md:py-4"
                        style={{ backgroundColor: meta.accent }}
                      >
                        ★ {meta.banner} ★
                      </div>
                    )}

                    <div className="relative z-10 p-4 md:p-8 flex flex-col flex-1">
                      {/* Tier number + line */}
                      <div className="flex items-center gap-3 mb-3 md:mb-5">
                        <span className="font-oswald font-bold text-xs tracking-widest" style={{ color: meta.accent }}>
                          {meta.number}
                        </span>
                        <div style={{ flex: 1, height: '1px', backgroundColor: meta.accent, opacity: 0.2 }} />
                      </div>

                      {/* Pack name */}
                      <div className="mb-3 md:mb-6">
                        <h3 className="font-oswald font-bold uppercase text-white leading-tight text-xl md:text-3xl">
                          {packData.name}
                        </h3>
                        <p className="font-manrope text-xs md:text-sm mt-1" style={{ color: meta.accent + 'BB' }}>
                          {packData.tagline}
                        </p>
                      </div>

                      {/* Participants + loyalty */}
                      <div className="flex flex-wrap gap-1.5 mb-3 md:mb-6">
                        <span className="font-oswald text-xs uppercase tracking-wider px-2 py-1 border" style={{ color: meta.accent, borderColor: meta.accent + '40' }}>
                          {packData.participants}
                        </span>
                        {packData.loyalty && (
                          <span className="font-oswald text-xs font-bold uppercase px-2 py-1 text-[#111]" style={{ backgroundColor: meta.accent }}>
                            {packData.loyalty}
                          </span>
                        )}
                      </div>

                      {/* Divider */}
                      <div style={{ height: '1px', backgroundColor: meta.accent, opacity: 0.12, marginBottom: '0.75rem' }} />

                      {/* Items */}
                      <ul className="space-y-1.5 md:space-y-3 flex-1 mb-4 md:mb-8">
                        {Array.isArray(items) && items.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 font-manrope text-white/60 text-xs md:text-sm">
                            <span style={{ color: meta.accent, flexShrink: 0, marginTop: '4px', fontSize: '7px' }}>▶</span>
                            {item}
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <Link
                        to="/produits"
                        className="flex items-center justify-center gap-2 font-oswald uppercase tracking-wider text-sm py-3 md:py-4 font-bold transition-opacity duration-200 hover:opacity-85"
                        style={{ backgroundColor: meta.accent, color: '#111111' }}
                        data-testid={`pack-cta-${meta.key}`}
                      >
                        {t('home.packs.cta')} <ChevronRight size={15} />
                      </Link>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>

          <ScrollReveal>
            <div className="text-center mt-10">
              <Link
                to="/produits"
                className="inline-flex items-center gap-2 border border-white/20 text-white/50 hover:border-[#F5820D] hover:text-[#F5820D] font-oswald uppercase tracking-widest text-sm px-8 py-3 transition-all duration-300"
                data-testid="see-all-products"
              >
                {t('home.packs.see_all')} <ChevronRight size={15} />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* VIP SECTION */}
      <section
        className="py-10 md:py-20 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/1571939/pexels-photo-1571939.jpeg?auto=compress&cs=tinysrgb&w=1600)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
        data-testid="vip-section"
      >
        {/* Dark overlay — stronger for readability */}
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(5, 15, 25, 0.88)' }} />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">

          <ScrollReveal>
            <span className="inline-block font-oswald text-xs uppercase tracking-widest text-[#F5820D] border border-[#F5820D]/50 px-4 py-1.5 mb-4 md:mb-8">
              Partnership UltraTiming × Ultra Sport
            </span>
            <h2
              className="font-oswald font-bold uppercase text-white leading-tight mb-5 md:mb-10"
              style={{ fontSize: 'clamp(1.4rem, 4vw, 2.8rem)', textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}
            >
              {t('home.vip.title')}
            </h2>
          </ScrollReveal>

          {/* Formula bar */}
          <ScrollReveal delay={100}>
            <div className="bg-[#F5820D] px-4 py-3 md:px-6 md:py-5 mb-5 md:mb-10">
              <p className="font-oswald font-bold uppercase text-[#111111] tracking-wider" style={{ fontSize: 'clamp(0.85rem, 2vw, 1.1rem)' }}>
                {t('home.vip.formula')}
              </p>
            </div>
          </ScrollReveal>

          {/* Description */}
          <ScrollReveal delay={150}>
            <p className="font-manrope text-white/80 text-sm max-w-2xl mx-auto mb-6 md:mb-12 leading-relaxed">
              {t('home.vip.description')}
            </p>
          </ScrollReveal>

          {/* 3 feature boxes */}
          <ScrollReveal delay={200}>
            <div className="grid grid-cols-3 gap-px mb-6 md:mb-12" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
              <div className="px-3 py-4 md:px-6 md:py-7 flex flex-col items-center gap-2 md:gap-3" style={{ backgroundColor: 'rgba(5,15,25,0.75)', borderTop: '3px solid #1B9BB0' }}>
                <Clock size={18} className="text-[#1B9BB0]" />
                <p className="font-oswald text-white text-xs uppercase tracking-widest leading-snug text-center">
                  {t('home.vip.timing_label')}
                </p>
              </div>
              <div className="px-3 py-4 md:px-6 md:py-7 flex flex-col items-center gap-2 md:gap-3" style={{ backgroundColor: 'rgba(20,14,0,0.85)', borderTop: '3px solid #F5C842' }}>
                <Flame size={18} className="text-[#F5C842]" />
                <p className="font-oswald text-[#F5C842] font-bold text-xs uppercase tracking-widest leading-snug text-center">
                  {t('home.vip.result_label')}
                </p>
              </div>
              <div className="px-3 py-4 md:px-6 md:py-7 flex flex-col items-center gap-2 md:gap-3" style={{ backgroundColor: 'rgba(5,15,25,0.75)', borderTop: '3px solid #F5820D' }}>
                <Package size={18} className="text-[#F5820D]" />
                <p className="font-oswald text-white text-xs uppercase tracking-widest leading-snug text-center">
                  {t('home.vip.goodies_label')}
                </p>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={300}>
            <a
              href="https://ultratiming.live"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#F5820D] hover:bg-[#D9730B] text-white font-oswald uppercase tracking-widest text-sm px-8 py-4 transition-all duration-300 hover:-translate-y-0.5"
              data-testid="vip-ultratiming-link"
            >
              {t('home.vip.discover')} <ExternalLink size={14} />
            </a>
          </ScrollReveal>

        </div>
      </section>



    </div>
  );
}
